import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import LegalFooter from "../components/LegalFooter";

export default function Register() {
  const { t } = useI18n();
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [consents, setConsents] = useState({ terms: false, privacy: false, telemed: false });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const allConsents = consents.terms && consents.privacy && consents.telemed;

  const submit = async (e) => {
    e.preventDefault();
    if (!allConsents) { toast.error("Debes aceptar los términos legales para continuar"); return; }
    setLoading(true);
    try {
      await register({
        ...form,
        role: "patient",
        consents: {
          ...consents,
          accepted_at: new Date().toISOString(),
        },
      });
      toast.success(t("saved"));
      nav("/app");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-8 bg-background">
      <form onSubmit={submit} className="w-full max-w-md fade-up" data-testid="register-form">
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Dr. Dimy Laguerre</Link>
        <div className="flex items-center gap-3 mt-4">
          <img src="/logo.svg" alt="" className="w-12 h-12 rounded-xl shadow-sm" />
          <div>
            <h1 className="font-display text-3xl leading-none">{t("register")}</h1>
            <div className="text-sm text-muted-foreground mt-1">{t("tagline")}</div>
          </div>
        </div>

        <div className="space-y-3 mt-6">
          <div>
            <Label>{t("name")}</Label>
            <Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="register-name" />
          </div>
          <div>
            <Label>{t("email")}</Label>
            <Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="register-email" />
          </div>
          <div>
            <Label>{t("password")}</Label>
            <Input type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="register-password" />
          </div>

          <div className="bg-[#F8F7F2] border border-border rounded-lg p-4 space-y-2.5 mt-3">
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input type="checkbox" required checked={consents.terms} onChange={(e) => setConsents((c) => ({ ...c, terms: e.target.checked }))} className="mt-0.5" data-testid="consent-terms" />
              <span>Acepto los <Link to="/terminos" target="_blank" className="text-primary underline">Términos y Condiciones</Link></span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input type="checkbox" required checked={consents.privacy} onChange={(e) => setConsents((c) => ({ ...c, privacy: e.target.checked }))} className="mt-0.5" data-testid="consent-privacy" />
              <span>He leído y acepto la <Link to="/privacidad" target="_blank" className="text-primary underline">Política de Privacidad</Link> y el tratamiento de mis datos de salud (RGPD Art. 9)</span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input type="checkbox" required checked={consents.telemed} onChange={(e) => setConsents((c) => ({ ...c, telemed: e.target.checked }))} className="mt-0.5" data-testid="consent-telemed" />
              <span>Doy mi <Link to="/consentimiento" target="_blank" className="text-primary underline">Consentimiento Informado para Telemedicina</Link></span>
            </label>
          </div>

          <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading || !allConsents} data-testid="register-submit-btn">
            {loading ? "..." : t("register")}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mt-6 text-center">
          <Link to="/login" className="text-primary hover:underline" data-testid="link-login">{t("login")}</Link>
        </p>
      </form>
      <div className="mt-8">
        <LegalFooter />
      </div>
    </div>
  );
}
